import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('connectmail_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Resolve an image URL to an absolute URL using the current backend.
 * Handles: relative paths (/api/images/...), old absolute URLs from previous deployments,
 * data: URLs, and already-correct absolute URLs.
 */
export const resolveImageUrl = (url) => {
  if (!url) return '';
  // data: URLs pass through
  if (url.startsWith('data:')) return url;
  // Already a relative path starting with /api/ — prepend current backend
  if (url.startsWith('/api/')) return `${BASE_URL}${url}`;
  // Absolute URL pointing to an old deployment domain — extract the path and rewrite
  try {
    const parsed = new URL(url);
    if (parsed.pathname.startsWith('/api/images/') || parsed.pathname.startsWith('/api/uploads/')) {
      return `${BASE_URL}${parsed.pathname}`;
    }
  } catch {
    // not a valid URL, return as-is
  }
  return url;
};

export const api = {
  // Password Reset
  requestPasswordReset: async (email) => {
    const response = await axios.post(`${API_URL}/auth/forgot-password`, { email, origin: window.location.origin });
    return response.data;
  },

  verifyResetToken: async (token) => {
    const response = await axios.get(`${API_URL}/auth/verify-reset-token/${token}`);
    return response.data;
  },

  resetPassword: async (token, newPassword) => {
    const response = await axios.post(`${API_URL}/auth/reset-password`, {
      token,
      new_password: newPassword
    });
    return response.data;
  },

  // Professions
  getProfessions: async () => {
    const response = await axios.get(`${API_URL}/professions`);
    return response.data;
  },

  // Dashboard
  getDashboardStats: async () => {
    const response = await axios.get(`${API_URL}/dashboard/stats`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // Signatures
  createSignature: async (data) => {
    // Used by RegisterPage for auto-creating the first signature after registration
    const response = await axios.post(`${API_URL}/signatures/generate`, {
      template_style: data.template_style || 'modern',
      color_scheme: 'navy',
      include_photo: false,
      include_social: false,
      base_url: BASE_URL
    }, {
      headers: getAuthHeaders()
    });
    return { ...response.data, name: data.name };
  },

  generateSignature: async (data) => {
    const response = await axios.post(`${API_URL}/signatures/generate`, {
      ...data,
      base_url: BASE_URL
    }, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  sendPreviewEmail: async (signatureId) => {
    const url = signatureId
      ? `${API_URL}/signatures/preview-email?signature_id=${encodeURIComponent(signatureId)}`
      : `${API_URL}/signatures/preview-email`;
    const response = await axios.post(url, {}, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  quickPreviewSignature: async (data) => {
    const response = await axios.post(`${API_URL}/signatures/quick-preview`, {
      ...data,
      base_url: BASE_URL
    }, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  getMySignatures: async () => {
    const response = await axios.get(`${API_URL}/signatures/my`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  exportSignature: async (signatureId) => {
    const response = await axios.get(`${API_URL}/signatures/${signatureId}/export?base_url=${encodeURIComponent(BASE_URL)}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  saveCustomSignature: async (data) => {
    const response = await axios.post(`${API_URL}/signatures/custom`, data, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  getBusinessCard: async () => {
    const response = await axios.get(`${API_URL}/users/me/business-card?base_url=${encodeURIComponent(BASE_URL)}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  getPublicBusinessCard: async (userId) => {
    const response = await axios.get(`${API_URL}/users/${userId}/business-card?base_url=${encodeURIComponent(BASE_URL)}`);
    return response.data;
  },

  cancelSubscription: async (reason) => {
    const response = await axios.post(`${API_URL}/subscription/cancel`, { reason }, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  getSubscriptionStatus: async () => {
    const response = await axios.get(`${API_URL}/subscription/status`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  getOpenSlots: async () => {
    const response = await axios.get(`${API_URL}/marketplace/open-slots`);
    return response.data;
  },

  // Public image upload (no auth required - for registration)
  uploadPublicImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API_URL}/upload/public`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  setMarketplaceSignature: async (signatureId) => {
    const response = await axios.post(`${API_URL}/users/marketplace-signature/${signatureId}`, {}, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  removeMarketplaceSignature: async () => {
    const response = await axios.delete(`${API_URL}/users/marketplace-signature`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  previewWithAds: async (signatureId) => {
    const response = await axios.get(`${API_URL}/signatures/${signatureId}/preview-with-ads?base_url=${encodeURIComponent(BASE_URL)}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  deleteSignature: async (signatureId) => {
    const response = await axios.delete(`${API_URL}/signatures/${signatureId}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // Marketplace
  getMarketplace: async () => {
    const response = await axios.get(`${API_URL}/ads/marketplace`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  placeBid: async (slotId, bidAmount, message) => {
    const response = await axios.post(`${API_URL}/ads/bid`, {
      slot_id: slotId,
      bid_amount: bidAmount,
      message
    }, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  getMyBids: async () => {
    const response = await axios.get(`${API_URL}/ads/my-bids`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  getMyPlacements: async () => {
    const response = await axios.get(`${API_URL}/ads/my-placements`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // Approvals
  getPendingApprovals: async () => {
    const response = await axios.get(`${API_URL}/approvals/pending`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  respondToApproval: async (requestId, approved, message) => {
    const response = await axios.post(`${API_URL}/approvals/respond`, {
      request_id: requestId,
      approved,
      message
    }, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  getActivePlacements: async () => {
    const response = await axios.get(`${API_URL}/ads/active-placements`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  revokePlacement: async (placementId) => {
    const response = await axios.post(`${API_URL}/ads/revoke-placement/${placementId}`, {}, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // Payments
  createCheckout: async (packageType, jobPostId) => {
    const response = await axios.post(`${API_URL}/payments/checkout`, {
      package_type: packageType,
      origin_url: window.location.origin,
      job_post_id: jobPostId || null,
    }, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  testActivate: async () => {
    const response = await axios.post(`${API_URL}/payments/test-activate`, {}, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  getPaymentStatus: async (sessionId) => {
    const response = await axios.get(`${API_URL}/payments/status/${sessionId}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  getPaymentHistory: async () => {
    const response = await axios.get(`${API_URL}/payments/history`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // User
  updateProfile: async (data) => {
    const response = await axios.put(`${API_URL}/users/profile`, data, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  getNotificationPreferences: async () => {
    const response = await axios.get(`${API_URL}/users/notification-preferences`, { headers: getAuthHeaders() });
    return response.data;
  },

  updateNotificationPreferences: async (preferences) => {
    const response = await axios.put(`${API_URL}/users/notification-preferences`, { preferences }, { headers: getAuthHeaders() });
    return response.data;
  },

  getMatchingProfessionals: async () => {
    const response = await axios.get(`${API_URL}/users/matching`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // Image Upload
  uploadImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API_URL}/uploads/image`, formData, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // Admin
  adminLogin: async (email, password) => {
    const response = await axios.post(`${API_URL}/admin/login`, { email, password });
    return response.data;
  },

  adminVerify: async (token) => {
    const response = await axios.get(`${API_URL}/admin/verify`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  adminGetDashboard: async (token) => {
    const response = await axios.get(`${API_URL}/admin/dashboard`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  adminGetPayouts: async (token) => {
    const response = await axios.get(`${API_URL}/admin/payouts`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  adminPayoutAction: async (token, payoutId, action) => {
    const response = await axios.post(`${API_URL}/admin/payouts/action`, {
      payout_id: payoutId, action
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  adminUpdateUser: async (token, userId, updates) => {
    const response = await axios.put(`${API_URL}/admin/users/${userId}`, updates, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  adminDeleteUser: async (token, userId) => {
    const response = await axios.delete(`${API_URL}/admin/users/${userId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  adminClearTestData: async (token) => {
    const response = await axios.delete(`${API_URL}/admin/clear-test-data`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Payouts
  requestPayout: async (amount, method) => {
    const response = await axios.post(`${API_URL}/payouts/request`, { amount, method }, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // Slots
  openSlot: async () => {
    const response = await axios.post(`${API_URL}/slots/open`, {}, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  closeSlot: async () => {
    const response = await axios.post(`${API_URL}/slots/close`, {}, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  getMySlots: async () => {
    const response = await axios.get(`${API_URL}/slots/my`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // Find a Pro
  searchProfessionals: async (params) => {
    const query = new URLSearchParams(params).toString();
    const response = await axios.get(`${API_URL}/users/search?${query}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // Matching
  getMyMatch: async () => {
    const response = await axios.get(`${API_URL}/users/my-match`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  getMyPayouts: async () => {
    const response = await axios.get(`${API_URL}/payouts/my`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // Analytics
  getMyAnalytics: async () => {
    const response = await axios.get(`${API_URL}/analytics/my`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // Extension
  getExtensionSignature: async () => {
    const response = await axios.get(`${API_URL}/extension/signature`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // Stripe Connect
  setupStripeConnect: async (originUrl) => {
    const response = await axios.post(`${API_URL}/connect/setup`, { origin_url: originUrl }, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  getConnectStatus: async () => {
    const response = await axios.get(`${API_URL}/connect/status`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  getConnectDashboard: async () => {
    const response = await axios.get(`${API_URL}/connect/dashboard`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // PayPal Payout
  savePayPalEmail: async (paypalEmail) => {
    const response = await axios.post(`${API_URL}/payout/paypal-email`, { paypal_email: paypalEmail }, { headers: getAuthHeaders() });
    return response.data;
  },
  updatePayoutMethod: async (method) => {
    const response = await axios.post(`${API_URL}/payout/method`, { method }, { headers: getAuthHeaders() });
    return response.data;
  },
  getPayoutInfo: async () => {
    const response = await axios.get(`${API_URL}/payout/info`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Feedback
  submitFeedback: async (subject, message, category) => {
    const response = await axios.post(`${API_URL}/feedback`, {
      subject,
      message,
      category
    }, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  adminGetFeedback: async (token) => {
    const response = await axios.get(`${API_URL}/admin/feedback`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  adminGetDemoAnalytics: async (token) => {
    const response = await axios.get(`${API_URL}/admin/demo-analytics-summary`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  adminGetFunnelSummary: async (token, windowDays = 7) => {
    const response = await axios.get(`${API_URL}/admin/funnel-summary?window_days=${windowDays}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  adminListSuccessStories: async (token) => {
    const response = await axios.get(`${API_URL}/admin/success-stories`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  adminUpdateSuccessStory: async (token, slot, updates) => {
    const response = await axios.put(`${API_URL}/admin/success-stories/${slot}`, updates, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  adminUploadSuccessVideo: async (token, slot, file, onProgress) => {
    const form = new FormData();
    form.append('file', file);
    const response = await axios.post(`${API_URL}/admin/success-stories/${slot}/upload`, form, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    });
    return response.data;
  },

  adminClearSuccessVideo: async (token, slot) => {
    const response = await axios.delete(`${API_URL}/admin/success-stories/${slot}/video`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  adminUpdateFeedback: async (token, feedbackId, updates) => {
    const response = await axios.put(`${API_URL}/admin/feedback/${feedbackId}`, updates, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  adminDeleteFeedback: async (token, feedbackId) => {
    const response = await axios.delete(`${API_URL}/admin/feedback/${feedbackId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Image History
  getImageHistory: async () => {
    const response = await axios.get(`${API_URL}/images/history`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  deleteImage: async (imageId) => {
    const response = await axios.delete(`${API_URL}/images/${imageId}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  getRecommendedMatches: async () => {
    const response = await axios.get(`${API_URL}/ads/recommended`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  cancelBid: async (bidId) => {
    const response = await axios.delete(`${API_URL}/ads/bids/${bidId}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  cancelPlacement: async (placementId) => {
    const response = await axios.post(`${API_URL}/ads/placements/${placementId}/cancel`, {}, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  getAdsOnMySignatures: async () => {
    const response = await axios.get(`${API_URL}/ads/on-my-signatures`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  removeAdFromSignature: async (placementId) => {
    const response = await axios.delete(`${API_URL}/ads/on-my-signatures/${placementId}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // Notifications
  getNotifications: async () => {
    const response = await axios.get(`${API_URL}/notifications`, { headers: getAuthHeaders() });
    return response.data;
  },
  getUnreadCount: async () => {
    const response = await axios.get(`${API_URL}/notifications/unread-count`, { headers: getAuthHeaders() });
    return response.data;
  },
  markNotificationRead: async (id) => {
    const response = await axios.post(`${API_URL}/notifications/${id}/read`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  markAllNotificationsRead: async () => {
    const response = await axios.post(`${API_URL}/notifications/read-all`, {}, { headers: getAuthHeaders() });
    return response.data;
  },

  // Live Signature
  getLiveSignature: async (userId) => {
    const response = await axios.get(`${API_URL}/signatures/live/${userId}?base_url=${encodeURIComponent(BASE_URL)}`);
    return response.data;
  },
  getSignatureVersion: async (signatureId) => {
    const response = await axios.get(`${API_URL}/signatures/version/${signatureId}`);
    return response.data;
  },

  // Feed
  getFeed: async (page = 1) => {
    const response = await axios.get(`${API_URL}/feed?page=${page}`, { headers: getAuthHeaders() });
    return response.data;
  },
  createFeedPost: async (content, postType = 'update', sharedUserId = null) => {
    const body = { content, post_type: postType };
    if (sharedUserId) body.shared_user_id = sharedUserId;
    const response = await axios.post(`${API_URL}/feed/posts`, body, { headers: getAuthHeaders() });
    return response.data;
  },
  toggleLikePost: async (postId) => {
    const response = await axios.post(`${API_URL}/feed/posts/${postId}/like`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  getComments: async (postId) => {
    const response = await axios.get(`${API_URL}/feed/posts/${postId}/comments`, { headers: getAuthHeaders() });
    return response.data;
  },
  addComment: async (postId, content) => {
    const response = await axios.post(`${API_URL}/feed/posts/${postId}/comments`, { content }, { headers: getAuthHeaders() });
    return response.data;
  },
  deleteFeedPost: async (postId) => {
    const response = await axios.delete(`${API_URL}/feed/posts/${postId}`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Chat
  getConversations: async () => {
    const response = await axios.get(`${API_URL}/chat/conversations`, { headers: getAuthHeaders() });
    return response.data;
  },
  createConversation: async (userId) => {
    const response = await axios.post(`${API_URL}/chat/conversations`, { user_id: userId }, { headers: getAuthHeaders() });
    return response.data;
  },
  getMessages: async (convoId, page = 1) => {
    const response = await axios.get(`${API_URL}/chat/conversations/${convoId}/messages?page=${page}`, { headers: getAuthHeaders() });
    return response.data;
  },
  sendMessage: async (convoId, content) => {
    const response = await axios.post(`${API_URL}/chat/conversations/${convoId}/messages`, { content }, { headers: getAuthHeaders() });
    return response.data;
  },
  getChatUnreadCount: async () => {
    const response = await axios.get(`${API_URL}/chat/unread-count`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Favorites
  addFavorite: async (userId) => {
    const response = await axios.post(`${API_URL}/users/favorites/${userId}`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  removeFavorite: async (userId) => {
    const response = await axios.delete(`${API_URL}/users/favorites/${userId}`, { headers: getAuthHeaders() });
    return response.data;
  },
  getFavorites: async () => {
    const response = await axios.get(`${API_URL}/users/favorites`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Connections
  sendConnectionRequest: async (userId) => {
    const response = await axios.post(`${API_URL}/connections/request/${userId}`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  acceptConnection: async (connId) => {
    const response = await axios.post(`${API_URL}/connections/${connId}/accept`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  declineConnection: async (connId) => {
    const response = await axios.post(`${API_URL}/connections/${connId}/decline`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  getConnections: async () => {
    const response = await axios.get(`${API_URL}/connections`, { headers: getAuthHeaders() });
    return response.data;
  },
  getConnectionStatus: async (userId) => {
    const response = await axios.get(`${API_URL}/connections/status/${userId}`, { headers: getAuthHeaders() });
    return response.data;
  },
  getReferralLink: async () => {
    const response = await axios.get(`${API_URL}/referrals/my-link`, { headers: getAuthHeaders() });
    return response.data;
  },
  getMyReferrals: async () => {
    const response = await axios.get(`${API_URL}/referrals/my-referrals`, { headers: getAuthHeaders() });
    return response.data;
  },
  importContacts: async (contacts) => {
    const response = await axios.post(`${API_URL}/referrals/import-contacts`, { contacts }, { headers: getAuthHeaders() });
    return response.data;
  },
  getMyInvites: async () => {
    const response = await axios.get(`${API_URL}/referrals/invites`, { headers: getAuthHeaders() });
    return response.data;
  },
  cancelReferralReward: async (rewardId) => {
    const response = await axios.post(`${API_URL}/referrals/cancel-reward/${rewardId}`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  updateSignature: async (signatureId, data) => {
    const response = await axios.put(`${API_URL}/signatures/${signatureId}`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  // Recommendations
  addRecommendation: async (userId, text, relationship='colleague') => {
    const response = await axios.post(`${API_URL}/users/${userId}/recommend`, { text, relationship }, { headers: getAuthHeaders() });
    return response.data;
  },
  getRecommendations: async (userId) => {
    const response = await axios.get(`${API_URL}/users/${userId}/recommendations`);
    return response.data;
  },
  deleteRecommendation: async (recId) => {
    const response = await axios.delete(`${API_URL}/recommendations/${recId}`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Profile Views
  logProfileView: async (userId) => {
    try { await axios.post(`${API_URL}/users/${userId}/view`, {}, { headers: getAuthHeaders() }); } catch {}
  },
  getMyProfileViews: async () => {
    const response = await axios.get(`${API_URL}/profile-views/my`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Events
  getEvents: async () => {
    const response = await axios.get(`${API_URL}/events`, { headers: getAuthHeaders() });
    return response.data;
  },
  createEvent: async (data) => {
    const response = await axios.post(`${API_URL}/events`, data, { headers: getAuthHeaders() });
    return response.data;
  },
  rsvpEvent: async (eventId) => {
    const response = await axios.post(`${API_URL}/events/${eventId}/rsvp`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  deleteEvent: async (eventId) => {
    const response = await axios.delete(`${API_URL}/events/${eventId}`, { headers: getAuthHeaders() });
    return response.data;
  },
  updateEvent: async (eventId, data) => {
    const response = await axios.put(`${API_URL}/events/${eventId}`, data, { headers: getAuthHeaders() });
    return response.data;
  },
  featureEvent: async (eventId) => {
    const response = await axios.post(`${API_URL}/events/${eventId}/feature`, {}, { headers: getAuthHeaders() });
    return response.data;
  },

  // Ambassadors
  getAmbassadors: async () => {
    const response = await axios.get(`${API_URL}/ambassadors`, { headers: getAuthHeaders() });
    return response.data;
  },
  adminToggleAmbassador: async (token, userId) => {
    const response = await axios.post(`${API_URL}/admin/ambassadors/${userId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
    return response.data;
  },

  // Groups
  getGroups: async () => {
    const response = await axios.get(`${API_URL}/groups`, { headers: getAuthHeaders() });
    return response.data;
  },
  createGroup: async (data) => {
    const response = await axios.post(`${API_URL}/groups`, data, { headers: getAuthHeaders() });
    return response.data;
  },
  getGroup: async (groupId) => {
    const response = await axios.get(`${API_URL}/groups/${groupId}`, { headers: getAuthHeaders() });
    return response.data;
  },
  joinGroup: async (groupId) => {
    const response = await axios.post(`${API_URL}/groups/${groupId}/join`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  getGroupPosts: async (groupId) => {
    const response = await axios.get(`${API_URL}/groups/${groupId}/posts`, { headers: getAuthHeaders() });
    return response.data;
  },
  createGroupPost: async (groupId, content) => {
    const response = await axios.post(`${API_URL}/groups/${groupId}/posts`, { content }, { headers: getAuthHeaders() });
    return response.data;
  },
  likeGroupPost: async (groupId, postId) => {
    const response = await axios.post(`${API_URL}/groups/${groupId}/posts/${postId}/like`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  getGroupMembers: async (groupId) => {
    const response = await axios.get(`${API_URL}/groups/${groupId}/members`, { headers: getAuthHeaders() });
    return response.data;
  },
  approveGroupMember: async (groupId, userId) => {
    const response = await axios.post(`${API_URL}/groups/${groupId}/approve/${userId}`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  rejectGroupMember: async (groupId, userId) => {
    const response = await axios.post(`${API_URL}/groups/${groupId}/reject/${userId}`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  kickGroupMember: async (groupId, userId) => {
    const response = await axios.post(`${API_URL}/groups/${groupId}/kick/${userId}`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  updateGroup: async (groupId, data) => {
    const response = await axios.put(`${API_URL}/groups/${groupId}`, data, { headers: getAuthHeaders() });
    return response.data;
  },
  commentGroupPost: async (groupId, postId, content) => {
    const response = await axios.post(`${API_URL}/groups/${groupId}/posts/${postId}/comment`, { content }, { headers: getAuthHeaders() });
    return response.data;
  },

  // Event Posts
  getEventPosts: async (eventId) => {
    const response = await axios.get(`${API_URL}/events/${eventId}/posts`, { headers: getAuthHeaders() });
    return response.data;
  },
  createEventPost: async (eventId, content) => {
    const response = await axios.post(`${API_URL}/events/${eventId}/posts`, { content }, { headers: getAuthHeaders() });
    return response.data;
  },
  likeEventPost: async (eventId, postId) => {
    const response = await axios.post(`${API_URL}/events/${eventId}/posts/${postId}/like`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  commentEventPost: async (eventId, postId, content) => {
    const response = await axios.post(`${API_URL}/events/${eventId}/posts/${postId}/comment`, { content }, { headers: getAuthHeaders() });
    return response.data;
  },
  getEvent: async (eventId) => {
    const response = await axios.get(`${API_URL}/events/${eventId}`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Group Events
  createGroupEvent: async (groupId, eventData) => {
    const response = await axios.post(`${API_URL}/groups/${groupId}/events`, eventData, { headers: getAuthHeaders() });
    return response.data;
  },
  getGroupEvents: async (groupId) => {
    const response = await axios.get(`${API_URL}/groups/${groupId}/events`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Learning Hub
  getArticles: async (category, search) => {
    const params = {}; if(category) params.category = category; if(search) params.search = search;
    const response = await axios.get(`${API_URL}/learning/articles`, { headers: getAuthHeaders(), params });
    return response.data;
  },
  createArticle: async (data) => {
    const response = await axios.post(`${API_URL}/learning/articles`, data, { headers: getAuthHeaders() });
    return response.data;
  },
  getArticle: async (articleId) => {
    const response = await axios.get(`${API_URL}/learning/articles/${articleId}`, { headers: getAuthHeaders() });
    return response.data;
  },
  likeArticle: async (articleId) => {
    const response = await axios.post(`${API_URL}/learning/articles/${articleId}/like`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  commentArticle: async (articleId, text) => {
    const response = await axios.post(`${API_URL}/learning/articles/${articleId}/comments`, { text }, { headers: getAuthHeaders() });
    return response.data;
  },
  deleteArticle: async (articleId) => {
    const response = await axios.delete(`${API_URL}/learning/articles/${articleId}`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Directory
  getDirectory: async ({ search, profession, city, page=1 } = {}) => {
    const params = { page }; if(search) params.search = search; if(profession) params.profession = profession; if(city) params.city = city;
    const response = await axios.get(`${API_URL}/directory`, { headers: getAuthHeaders(), params });
    return response.data;
  },

  // Smart Connect
  getConnectPackages: async () => {
    const response = await axios.get(`${API_URL}/connect/packages`, { headers: getAuthHeaders() });
    return response.data;
  },
  getConnectCredits: async () => {
    const response = await axios.get(`${API_URL}/connect/credits`, { headers: getAuthHeaders() });
    return response.data;
  },
  sendConnectRequest: async (recipientId, message) => {
    const response = await axios.post(`${API_URL}/connect/send`, { recipient_id: recipientId, message }, { headers: getAuthHeaders() });
    return response.data;
  },
  getConnectRequests: async () => {
    const response = await axios.get(`${API_URL}/connect/my-requests`, { headers: getAuthHeaders() });
    return response.data;
  },
  subscribeConnectPackage: async (packageId) => {
    const response = await axios.post(`${API_URL}/connect/subscribe?package_id=${packageId}`, {}, { headers: getAuthHeaders() });
    return response.data;
  },

  // AI Coach
  coachChat: async (sessionId, message) => {
    const response = await axios.post(`${API_URL}/ai-coach/coach-chat`, { session_id: sessionId, message }, { headers: getAuthHeaders() });
    return response.data;
  },
  getAIProfileTips: async () => {
    const response = await axios.post(`${API_URL}/ai-coach/tips`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  generateAIMessage: async (data) => {
    const response = await axios.post(`${API_URL}/ai-coach/generate-message`, data, { headers: getAuthHeaders() });
    return response.data;
  },
  getAIMatchSuggestions: async () => {
    const response = await axios.get(`${API_URL}/ai-coach/match-suggestions`, { headers: getAuthHeaders() });
    return response.data;
  },
  generateIcebreaker: async (targetUserId) => {
    const response = await axios.post(`${API_URL}/ai-coach/icebreaker?target_user_id=${targetUserId}`, {}, { headers: getAuthHeaders() });
    return response.data;
  },

  // Smart Matching
  getRecommendedProfessionals: async (limit = 10, options = {}) => {
    const params = new URLSearchParams({ limit });
    if (options.nearMe) params.set('near_me', 'true');
    if (options.sort) params.set('sort', options.sort);
    const response = await axios.get(`${API_URL}/directory/recommended?${params}`, { headers: getAuthHeaders() });
    return response.data;
  },

  getNearbyProfessionals: async (limit = 10) => {
    const params = new URLSearchParams({ limit, near_me: 'true' });
    const response = await axios.get(`${API_URL}/directory/recommended?${params}`, { headers: getAuthHeaders() });
    return { professionals: response.data.recommended || [] };
  },

  getRisingStars: async (limit = 10) => {
    const params = new URLSearchParams({ limit, sort: 'newest' });
    const response = await axios.get(`${API_URL}/directory/recommended?${params}`, { headers: getAuthHeaders() });
    return { stars: response.data.recommended || [] };
  },

  // Accept/Decline Connections
  acceptConnectionRequest: async (requestId) => {
    const response = await axios.post(`${API_URL}/connections/${requestId}/accept`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  declineConnectionRequest: async (requestId) => {
    const response = await axios.post(`${API_URL}/connections/${requestId}/decline`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  getPendingConnections: async () => {
    const response = await axios.get(`${API_URL}/connections/pending`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Google Contact Import
  getGoogleContactsAuthUrl: async () => {
    const origin = window.location.origin;
    const response = await axios.get(`${API_URL}/contacts/google/auth-url?origin=${encodeURIComponent(origin)}`, { headers: getAuthHeaders() });
    return response.data;
  },
  getMicrosoftContactsAuthUrl: async () => {
    const origin = window.location.origin;
    const response = await axios.get(`${API_URL}/contacts/microsoft/auth-url?origin=${encodeURIComponent(origin)}`, { headers: getAuthHeaders() });
    return response.data;
  },
  getContactImportResults: async () => {
    const response = await axios.get(`${API_URL}/contacts/import-results`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Phone OTP Verification (Twilio)
  sendOTP: async (phone) => {
    const response = await axios.post(`${API_URL}/auth/send-otp`, { phone });
    return response.data;
  },
  verifyOTP: async (phone, code) => {
    const response = await axios.post(`${API_URL}/auth/verify-otp`, { phone, code });
    return response.data;
  },

  // Email OTP Verification (free alternative to SMS)
  sendEmailOTP: async (email) => {
    const response = await axios.post(`${API_URL}/auth/send-email-otp`, { email });
    return response.data;
  },
  checkEmail: async (email) => {
    const response = await axios.post(`${API_URL}/auth/check-email`, { email });
    return response.data;
  },
  verifyEmailOTP: async (email, code) => {
    const response = await axios.post(`${API_URL}/auth/verify-email-otp`, { email, code });
    return response.data;
  },


  // Community Introductions
  postCommunityIntro: async (bio, city, state) => {
    const response = await axios.post(`${API_URL}/community/introduce`, { bio, city, state }, { headers: getAuthHeaders() });
    return response.data;
  },
  getCommunityIntros: async (city) => {
    const params = city ? `?city=${encodeURIComponent(city)}` : '';
    const response = await axios.get(`${API_URL}/community/introductions${params}`, { headers: getAuthHeaders() });
    return response.data;
  },
  likeCommunityIntro: async (introId) => {
    const response = await axios.post(`${API_URL}/community/introductions/${introId}/like`, {}, { headers: getAuthHeaders() });
    return response.data;
  },

  // Groups with filter
  getGroupsFiltered: async (filter) => {
    const params = filter && filter !== 'all' ? `?filter=${filter}` : '';
    const response = await axios.get(`${API_URL}/groups${params}`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Enhanced Profile View
  logProfileViewEnhanced: async (viewedUserId) => {
    const response = await axios.post(`${API_URL}/profile-view/${viewedUserId}`, {}, { headers: getAuthHeaders() });
    return response.data;
  },

  // Marketplace — Waitlist
  joinWaitlist: async (targetUserId, message = '') => {
    const response = await axios.post(`${API_URL}/marketplace/waitlist/join`, { target_user_id: targetUserId, message }, { headers: getAuthHeaders() });
    return response.data;
  },
  getWaitlist: async (userId) => {
    const response = await axios.get(`${API_URL}/marketplace/waitlist/${userId}`, { headers: getAuthHeaders() });
    return response.data;
  },
  getMyWaitlistEntries: async () => {
    const response = await axios.get(`${API_URL}/marketplace/waitlist/my/entries`, { headers: getAuthHeaders() });
    return response.data;
  },
  leaveWaitlist: async (entryId) => {
    const response = await axios.delete(`${API_URL}/marketplace/waitlist/${entryId}`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Marketplace — Premium Bidding
  placePremiumBid: async (targetUserId, bidAmount, message = '') => {
    const response = await axios.post(`${API_URL}/marketplace/bid/premium`, { target_user_id: targetUserId, bid_amount: bidAmount, message }, { headers: getAuthHeaders() });
    return response.data;
  },
  getPremiumBids: async (userId) => {
    const response = await axios.get(`${API_URL}/marketplace/bids/${userId}`, { headers: getAuthHeaders() });
    return response.data;
  },
  acceptPremiumBid: async (bidId) => {
    const response = await axios.post(`${API_URL}/marketplace/bids/${bidId}/accept`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  declinePremiumBid: async (bidId) => {
    const response = await axios.post(`${API_URL}/marketplace/bids/${bidId}/decline`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  enableBidding: async () => {
    const response = await axios.post(`${API_URL}/marketplace/bidding/enable`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  getSlotStatus: async (userId) => {
    const response = await axios.get(`${API_URL}/marketplace/slot-status/${userId}`, { headers: getAuthHeaders() });
    return response.data;
  },
  updateSlotPricing: async (slotPosition, pricePerMonth) => {
    const response = await axios.put(`${API_URL}/marketplace/slot-pricing`, { slot_position: slotPosition, price_per_month: pricePerMonth }, { headers: getAuthHeaders() });
    return response.data;
  },
  getMySlotPricing: async () => {
    const response = await axios.get(`${API_URL}/marketplace/my-slot-pricing`, { headers: getAuthHeaders() });
    return response.data;
  },
  sendContactInvites: async (contactIds, customMessage = '') => {
    const response = await axios.post(`${API_URL}/contacts/send-invites`, { contact_ids: contactIds, custom_message: customMessage }, { headers: getAuthHeaders() });
    return response.data;
  },
  sendAllInvites: async (customMessage = '', selectedEmails = []) => {
    const response = await axios.post(`${API_URL}/contacts/send-all-invites`, { custom_message: customMessage, selected_emails: selectedEmails, origin: window.location.origin }, { headers: getAuthHeaders() });
    return response.data;
  },
  adminGetContacts: async (token) => {
    const response = await axios.get(`${API_URL}/admin/contacts`, { headers: { Authorization: `Bearer ${token}` } });
    return response.data;
  },
  adminSendPromoEmail: async (token, subject, body, recipientFilter = 'all', testMode = false) => {
    const response = await axios.post(`${API_URL}/admin/send-promo-email`, { subject, body, recipient_filter: recipientFilter, test_mode: testMode }, { headers: { Authorization: `Bearer ${token}` } });
    return response.data;
  },
  adminGetPromoCampaigns: async (token) => {
    const response = await axios.get(`${API_URL}/admin/promo-campaigns`, { headers: { Authorization: `Bearer ${token}` } });
    return response.data;
  },
  adminClearTestData: async (token) => {
    const response = await axios.post(`${API_URL}/admin/clear-test-data`, {}, { headers: { Authorization: `Bearer ${token}` } });
    return response.data;
  },
  adminGetCrmDatabase: async (token) => {
    const response = await axios.get(`${API_URL}/admin/crm-database`, { headers: { Authorization: `Bearer ${token}` } });
    return response.data;
  },
  adminGeneratePromoVideo: async (token, { prompt, duration } = {}) => {
    const response = await axios.post(`${API_URL}/admin/generate-promo-video`, { prompt, duration }, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
    return response.data;
  },
  adminGenerateVideo: async (token, { prompt, duration, video_type } = {}) => {
    const response = await axios.post(`${API_URL}/admin/generate-video`, { prompt, duration, video_type }, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
    return response.data;
  },
  adminGetVideoStatus: async (token, jobId) => {
    const response = await axios.get(`${API_URL}/admin/video-status/${jobId}`, { headers: { Authorization: `Bearer ${token}` } });
    return response.data;
  },
  adminToggleVideoPublish: async (token, videoType) => {
    const response = await axios.post(`${API_URL}/admin/toggle-video-publish`, { video_type: videoType || 'promo' }, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
    return response.data;
  },
  adminGenerateVideoPrompt: async (token, { description, duration, video_type } = {}) => {
    const response = await axios.post(`${API_URL}/admin/generate-video-prompt`, { description, duration, video_type }, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
    return response.data;
  },
  adminListVideos: async (token) => {
    const response = await axios.get(`${API_URL}/admin/videos`, { headers: { Authorization: `Bearer ${token}` } });
    return response.data;
  },
  adminGenerateSlideshowVideo: async (token, { video_type, scenes, seconds_per_scene } = {}) => {
    const response = await axios.post(`${API_URL}/admin/generate-slideshow-video`, { video_type, scenes, seconds_per_scene }, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
    return response.data;
  },

  // Engagement - Local Discovery
  discoverLocal: async (category = null) => {
    const params = category ? `?category=${category}` : '';
    const response = await axios.get(`${API_URL}/discover/local${params}`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Engagement - Partner Suggestions
  getPartnerSuggestions: async () => {
    const response = await axios.get(`${API_URL}/partner-suggestions`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Gamification - Badges
  getMyBadges: async () => {
    const response = await axios.get(`${API_URL}/gamification/my-badges`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Gamification - Leaderboard
  getLeaderboard: async () => {
    const response = await axios.get(`${API_URL}/gamification/leaderboard`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Recommendations - Request
  requestRecommendation: async (targetUserId, message = '') => {
    const response = await axios.post(`${API_URL}/recommendations/request/${targetUserId}`, { message }, { headers: getAuthHeaders() });
    return response.data;
  },

  // Recommendations - Write
  writeRecommendation: async (requestId, text) => {
    const response = await axios.post(`${API_URL}/recommendations/write/${requestId}`, { text }, { headers: getAuthHeaders() });
    return response.data;
  },

  // Recommendations - My
  getMyRecommendationRequests: async () => {
    const response = await axios.get(`${API_URL}/recommendations/my`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Engagement - Check Reminders (admin)
  checkEngagementReminders: async () => {
    const response = await axios.post(`${API_URL}/engagement/check-reminders`, {}, { headers: getAuthHeaders() });
    return response.data;
  },

  // Engagement - Send Automated Emails (admin)
  sendAutomatedEmails: async () => {
    const response = await axios.post(`${API_URL}/engagement/send-automated-emails`, {}, { headers: getAuthHeaders() });
    return response.data;
  },

  // Business APIs
  getBusinessDirectory: async () => {
    const response = await axios.get(`${API_URL}/business/directory`, { headers: getAuthHeaders() });
    return response.data;
  },
  getBusinessPartners: async () => {
    const response = await axios.get(`${API_URL}/business/partners`, { headers: getAuthHeaders() });
    return response.data;
  },
  linkToBusiness: async (businessId) => {
    const response = await axios.post(`${API_URL}/business/link`, { business_id: businessId }, { headers: getAuthHeaders() });
    return response.data;
  },
  unlinkFromBusiness: async () => {
    const response = await axios.post(`${API_URL}/business/unlink`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  updateBusinessProfile: async (data) => {
    const response = await axios.put(`${API_URL}/business/profile`, data, { headers: getAuthHeaders() });
    return response.data;
  },
  createRecruitingAd: async (data) => {
    const response = await axios.post(`${API_URL}/business/recruiting-ads`, data, { headers: getAuthHeaders() });
    return response.data;
  },
  getMyRecruitingAds: async () => {
    const response = await axios.get(`${API_URL}/business/recruiting-ads`, { headers: getAuthHeaders() });
    return response.data;
  },
  getRecruitingAdsFeed: async () => {
    const response = await axios.get(`${API_URL}/business/recruiting-ads/feed`, { headers: getAuthHeaders() });
    return response.data;
  },
  deleteRecruitingAd: async (adId) => {
    const response = await axios.delete(`${API_URL}/business/recruiting-ads/${adId}`, { headers: getAuthHeaders() });
    return response.data;
  },
  aiAssistAd: async () => {
    const response = await axios.post(`${API_URL}/business/recruiting-ads/ai-assist`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  // Ownership (Claim / Invite)
  claimBusiness: async (businessId) => {
    const response = await axios.post(`${API_URL}/business/claim`, { business_id: businessId }, { headers: getAuthHeaders() });
    return response.data;
  },
  releaseOwnership: async () => {
    const response = await axios.post(`${API_URL}/business/release-ownership`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  inviteOwner: async (individualId) => {
    const response = await axios.post(`${API_URL}/business/invite-owner`, { individual_id: individualId }, { headers: getAuthHeaders() });
    return response.data;
  },
  getOwnershipInvites: async () => {
    const response = await axios.get(`${API_URL}/business/ownership-invites`, { headers: getAuthHeaders() });
    return response.data;
  },
  acceptOwnership: async (inviteId) => {
    const response = await axios.post(`${API_URL}/business/accept-ownership/${inviteId}`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  declineOwnership: async (inviteId) => {
    const response = await axios.post(`${API_URL}/business/decline-ownership/${inviteId}`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  getMyBusiness: async () => {
    const response = await axios.get(`${API_URL}/business/my-business`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Gmail Auto-Sync
  gmailConnect: async () => {
    const response = await axios.get(`${API_URL}/oauth/gmail/connect`, { headers: getAuthHeaders() });
    return response.data;
  },
  gmailStatus: async () => {
    const response = await axios.get(`${API_URL}/oauth/gmail/status`, { headers: getAuthHeaders() });
    return response.data;
  },
  gmailDisconnect: async () => {
    const response = await axios.post(`${API_URL}/oauth/gmail/disconnect`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  pushToGmail: async (signatureId) => {
    const params = signatureId ? `?signature_id=${signatureId}` : '';
    const response = await axios.post(`${API_URL}/signatures/push-to-gmail${params}`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  checkSignatureUpdate: async () => {
    const response = await axios.get(`${API_URL}/ads/signature-update-pending`, { headers: getAuthHeaders() });
    return response.data;
  },
  dismissSignatureUpdate: async () => {
    const response = await axios.post(`${API_URL}/ads/signature-update-dismiss`, {}, { headers: getAuthHeaders() });
    return response.data;
  },

  // Hiring / Job Posts
  getMyJobPosts: async () => {
    const response = await axios.get(`${API_URL}/hiring/posts`, { headers: getAuthHeaders() });
    return response.data;
  },
  createJobPost: async (data) => {
    const response = await axios.post(`${API_URL}/hiring/posts`, data, { headers: getAuthHeaders() });
    return response.data;
  },
  updateJobPost: async (postId, data) => {
    const response = await axios.put(`${API_URL}/hiring/posts/${postId}`, data, { headers: getAuthHeaders() });
    return response.data;
  },
  aiGenerateJob: async (prompt) => {
    const response = await axios.post(`${API_URL}/hiring/ai-generate-job`, { prompt }, { headers: getAuthHeaders() });
    return response.data;
  },
  aiGenerateJobImage: async (prompt) => {
    const response = await axios.post(`${API_URL}/hiring/ai-generate-image`, { prompt }, { headers: getAuthHeaders() });
    return response.data;
  },
  deleteJobPost: async (postId) => {
    const response = await axios.delete(`${API_URL}/hiring/posts/${postId}`, { headers: getAuthHeaders() });
    return response.data;
  },
  getJobPost: async (postId) => {
    const response = await axios.get(`${API_URL}/hiring/posts/${postId}`, { headers: getAuthHeaders() });
    return response.data;
  },
  getJobFeed: async () => {
    const response = await axios.get(`${API_URL}/jobs/feed`, { headers: getAuthHeaders() });
    return response.data;
  },
  applyToJob: async (postId) => {
    const response = await axios.post(`${API_URL}/jobs/${postId}/apply`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  submitApplication: async (postId, formData) => {
    const response = await axios.post(`${API_URL}/jobs/${postId}/submit-application`, formData, {
      headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  updateApplicationStatus: async (appId, status) => {
    const response = await axios.put(`${API_URL}/hiring/applications/${appId}/status?status=${status}`, {}, { headers: getAuthHeaders() });
    return response.data;
  },
  getHiringActivation: async () => {
    const response = await axios.get(`${API_URL}/hiring/activation-status`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Employee Management
  getEmployees: async () => {
    const response = await axios.get(`${API_URL}/business/employees`, { headers: getAuthHeaders() });
    return response.data;
  },
  inviteEmployee: async (email) => {
    const response = await axios.post(`${API_URL}/business/employees/invite`, { email }, { headers: getAuthHeaders() });
    return response.data;
  },
  removeEmployee: async (employeeId) => {
    const response = await axios.post(`${API_URL}/business/employees/remove`, { employee_id: employeeId }, { headers: getAuthHeaders() });
    return response.data;
  },
  cancelInvite: async (inviteId) => {
    const response = await axios.delete(`${API_URL}/business/employees/invite/${inviteId}`, { headers: getAuthHeaders() });
    return response.data;
  },
  uploadDocument: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API_URL}/uploads/document`, formData, {
      headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

};

export default api;
